"use client";

import { createFileRoute } from "@tanstack/react-router";
import { motion } from "framer-motion";
import { ArrowRight, Building2, Mail, MapPin, Phone, Send } from "lucide-react";
import { type FormEvent, useId, useState } from "react";

import {
	MapMarker,
	Map as MapView,
	MarkerContent,
	MarkerPopup,
} from "@/components/ui/map";
import { useLang, useLangKey } from "@/contexts/lang";
import { hotels } from "@/data/home";
import { submitContact } from "@/lib/submitContact";

export const Route = createFileRoute("/contact")({
	headers: () => ({
		"Cache-Control":
			"public, max-age=0, s-maxage=3600, stale-while-revalidate=86400",
	}),
	head: () => ({
		meta: [
			{ title: "Контакт | Македонијатурист АД" },
			{
				name: "description",
				content:
					"Контактирајте со Македонијатурист АД. Информации за нашите хотели, резервации и деловна соработка. Ве очекуваме.",
			},
		],
		links: [
			{
				rel: "canonical",
				href: "https://makedonijaturist.com.mk/contact",
			},
		],
	}),
	component: ContactPage,
});

const hotelContacts = [
	{
		nameMk: "Holiday Inn Скопје",
		nameEn: "Holiday Inn Skopje",
		phoneHref: "tel:+38923292929",
		phoneLabel: "+389 2 329 29 29",
		emailHref: "mailto:hiskopje@hiskopje.mk",
		emailLabel: "hiskopje@hiskopje.mk",
	},
	{
		nameMk: "Хотел Турист",
		nameEn: "Hotel Turist",
		phoneHref: "tel:+38923289111",
		phoneLabel: "+389 2 3289 111",
		emailHref: "mailto:bestwestern@hotelturist.com.mk",
		emailLabel: "bestwestern@hotelturist.com.mk",
	},
	{
		nameMk: "Хотел Карпош",
		nameEn: "Hotel Karposh",
		phoneHref: "tel:+38923088388",
		phoneLabel: "+389 2 3088 388",
		emailHref: "mailto:hotelkarpos@hotelkarpos.com.mk",
		emailLabel: "hotelkarpos@hotelkarpos.com.mk",
	},
	{
		nameMk: "Хотел Bellevue",
		nameEn: "Hotel Bellevue",
		phoneHref: "tel:+38922573880",
		phoneLabel: "+389 2 2573 880",
		emailHref: "mailto:info@bellevue.com.mk",
		emailLabel: "info@bellevue.com.mk",
	},
	{
		nameMk: "Хотел Водно",
		nameEn: "Hotel Vodno",
		phoneHref: "tel:+38923177027",
		phoneLabel: "+389 2 3177 027",
		emailHref: "mailto:info@hotelvodno.com.mk",
		emailLabel: "info@hotelvodno.com.mk",
	},
];

const bounds = hotels.reduce(
	(acc, hotel) => ({
		minLat: Math.min(acc.minLat, hotel.latitude),
		maxLat: Math.max(acc.maxLat, hotel.latitude),
		minLng: Math.min(acc.minLng, hotel.longitude),
		maxLng: Math.max(acc.maxLng, hotel.longitude),
	}),
	{
		minLat: Number.POSITIVE_INFINITY,
		maxLat: Number.NEGATIVE_INFINITY,
		minLng: Number.POSITIVE_INFINITY,
		maxLng: Number.NEGATIVE_INFINITY,
	},
);

const hotelMapBounds: [[number, number], [number, number]] = [
	[bounds.minLng, bounds.minLat],
	[bounds.maxLng, bounds.maxLat],
];

const hotelMapCenter: [number, number] = [
	(bounds.minLng + bounds.maxLng) / 2,
	(bounds.minLat + bounds.maxLat) / 2,
];

const hotelInitialView = {
	bounds: hotelMapBounds,
	padding: 64,
};

export function ContactPage() {
	const formId = useId();
	const t = useLang();
	const lang = useLangKey();
	const nameId = `${formId}-name`;
	const emailId = `${formId}-email`;
	const phoneId = `${formId}-phone`;
	const hotelId = `${formId}-hotel`;
	const messageId = `${formId}-message`;

	const [attempted, setAttempted] = useState(false);
	const [name, setName] = useState("");
	const [email, setEmail] = useState("");
	const [phone, setPhone] = useState("");
	const [hotel, setHotel] = useState("");
	const [message, setMessage] = useState("");
	const [status, setStatus] = useState<
		"idle" | "loading" | "success" | "error"
	>("idle");

	const req = lang === "en" ? "Required" : "Задолжително";
	const nameErr = attempted && !name;
	const emailErr = attempted && !email;
	const phoneErr = attempted && !phone;
	const hotelErr = attempted && !hotel;
	const messageErr = attempted && !message;

	async function handleSubmit(e: FormEvent<HTMLFormElement>) {
		e.preventDefault();
		setAttempted(true);
		if (!name || !email || !phone || !hotel || !message) return;

		setStatus("loading");
		try {
			await submitContact({
				data: { name, email, phone, hotel, message },
			});
			setStatus("success");
		} catch (err) {
			console.error("Failed to submit contact form:", err);
			setStatus("error");
		}
	}

	const labelClassName = "block text-sm font-medium text-foreground mb-2";
	const fieldBase =
		"w-full rounded-xl border bg-background px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-4 transition-all";
	const fieldOk = "border-input focus-visible:ring-ring/30";
	const fieldErr =
		"border-destructive ring-4 ring-destructive/20 focus-visible:ring-destructive/30";

	return (
		<main className="bg-background">
			<section className="mt-grain relative overflow-hidden bg-primary text-white">
				{/* Animated background elements */}
				<div className="pointer-events-none absolute inset-0">
					{/* Main gold glow - animated */}
					<motion.div
						className="absolute -top-[20%] right-0 h-[400px] w-[400px] sm:h-[600px] sm:w-[600px] md:h-[800px] md:w-[800px] rounded-full bg-[radial-gradient(closest-side,rgba(201,161,90,0.15),transparent)] blur-[60px] sm:blur-[80px] md:blur-[100px]"
						animate={{
							scale: [1, 1.2, 1],
							opacity: [0.5, 0.8, 0.5],
						}}
						transition={{
							duration: 8,
							repeat: Number.POSITIVE_INFINITY,
							ease: "easeInOut",
						}}
					/>
					{/* Secondary white glow - animated */}
					<motion.div
						className="absolute bottom-0 left-[-10%] h-[300px] w-[300px] sm:h-[450px] sm:w-[450px] md:h-[600px] md:w-[600px] rounded-full bg-[radial-gradient(closest-side,rgba(255,255,255,0.08),transparent)] blur-[50px] sm:blur-[65px] md:blur-[80px]"
						animate={{
							scale: [1, 1.15, 1],
							x: [0, 30, 0],
						}}
						transition={{
							duration: 10,
							repeat: Number.POSITIVE_INFINITY,
							ease: "easeInOut",
						}}
					/>
					{/* Additional floating accent */}
					<motion.div
						className="absolute top-[40%] left-[20%] h-[150px] w-[150px] sm:h-[225px] sm:w-[225px] md:h-[300px] md:w-[300px] rounded-full bg-[radial-gradient(closest-side,rgba(201,161,90,0.08),transparent)] blur-[40px] sm:blur-[50px] md:blur-[60px]"
						animate={{
							y: [-20, 20, -20],
							x: [-10, 10, -10],
						}}
						transition={{
							duration: 12,
							repeat: Number.POSITIVE_INFINITY,
							ease: "easeInOut",
						}}
					/>
					{/* Subtle grid overlay */}
					<div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:40px_40px] sm:bg-[size:50px_50px] md:bg-[size:60px_60px] [mask-image:radial-gradient(ellipse_at_center,black_20%,transparent_70%)]" />
				</div>
				<div className="relative mx-auto max-w-7xl px-4 sm:px-6 py-12 sm:py-16 md:py-28">
					<div className="max-w-4xl">
						<p className="text-xs font-bold tracking-[0.2em] uppercase text-accent">
							{t.contactPage.hero.label}
						</p>
						<h1 className="mt-4 sm:mt-6 font-display text-3xl sm:text-4xl md:text-5xl lg:text-7xl font-medium tracking-tight leading-[1.08]">
							{t.contactPage.hero.heading}
						</h1>
						<p className="mt-4 sm:mt-6 max-w-3xl text-base sm:text-lg text-white/80 font-light leading-relaxed md:text-xl">
							{t.contactPage.hero.intro}
						</p>
					</div>
				</div>
			</section>

			<section className="bg-secondary py-12 sm:py-16 md:py-24">
				<div className="mx-auto max-w-7xl px-4 sm:px-6">
					{/* Two column layout: Form + Info */}
					<div className="grid gap-6 lg:grid-cols-2">
						{/* Form */}
						<div className="h-full flex flex-col overflow-hidden rounded-xl md:rounded-2xl border border-border bg-card shadow-sm">
							<div className="h-1 bg-accent shrink-0" />
							<div className="p-5 sm:p-6 md:p-8 flex-1 flex flex-col">
								<div className="flex items-start justify-between gap-4">
									<div>
										<p className="text-xs font-bold tracking-[0.2em] uppercase text-accent">
											{t.contactPage.form.label}
										</p>
										<h2 className="mt-3 font-display text-xl sm:text-2xl font-medium tracking-tight text-primary">
											{t.contactPage.form.heading}
										</h2>
									</div>
									<div className="hidden sm:inline-flex h-10 w-10 items-center justify-center rounded-full bg-primary/5 text-primary">
										<Send className="h-5 w-5" />
									</div>
								</div>

								{status === "success" ? (
									<div className="flex-1 flex flex-col items-center justify-center py-12 text-center gap-4">
										<div className="flex h-16 w-16 items-center justify-center rounded-full bg-accent/10">
											<Send className="h-8 w-8 text-accent" />
										</div>
										<h3 className="font-display text-2xl font-medium text-primary">
											{lang === "en"
												? "Message Sent!"
												: "Пораката е испратена!"}
										</h3>
										<p className="text-muted-foreground max-w-sm">
											{lang === "en"
												? "Thank you for reaching out. We will get back to you as soon as possible."
												: "Ви благодариме за пораката. Ќе ве контактираме во најбрз можен рок."}
										</p>
										<button
											type="button"
											onClick={() => {
												setStatus("idle");
												setAttempted(false);
												setName("");
												setEmail("");
												setPhone("");
												setHotel("");
												setMessage("");
											}}
											className="mt-4 text-sm font-semibold text-primary hover:text-accent transition-colors"
										>
											{lang === "en"
												? "Send another message"
												: "Испрати друга порака"}
										</button>
									</div>
								) : (
									<form
										onSubmit={handleSubmit}
										noValidate
										className="mt-6 flex-1 flex flex-col gap-4"
									>
										<div className="grid gap-4 sm:grid-cols-2">
											<div>
												<label htmlFor={nameId} className={labelClassName}>
													{t.contactPage.form.nameLabel}{" "}
													<span className="text-destructive">*</span>
												</label>
												<input
													type="text"
													id={nameId}
													name="name"
													autoComplete="name"
													value={name}
													onChange={(e) => setName(e.target.value)}
													className={`${fieldBase} ${nameErr ? fieldErr : fieldOk}`}
													placeholder={t.contactPage.form.namePlaceholder}
												/>
												{nameErr && (
													<p className="text-destructive text-xs mt-1">{req}</p>
												)}
											</div>
											<div>
												<label htmlFor={emailId} className={labelClassName}>
													{t.contactPage.form.emailLabel}{" "}
													<span className="text-destructive">*</span>
												</label>
												<input
													type="email"
													id={emailId}
													name="email"
													autoComplete="email"
													inputMode="email"
													value={email}
													onChange={(e) => setEmail(e.target.value)}
													className={`${fieldBase} ${emailErr ? fieldErr : fieldOk}`}
													placeholder="vase.ime@example.com"
												/>
												{emailErr && (
													<p className="text-destructive text-xs mt-1">{req}</p>
												)}
											</div>
										</div>

										<div className="grid gap-4 sm:grid-cols-2">
											<div>
												<label htmlFor={phoneId} className={labelClassName}>
													{t.contactPage.form.phoneLabel}{" "}
													<span className="text-destructive">*</span>
												</label>
												<input
													type="tel"
													id={phoneId}
													name="phone"
													autoComplete="tel"
													inputMode="tel"
													value={phone}
													onChange={(e) => setPhone(e.target.value)}
													className={`${fieldBase} ${phoneErr ? fieldErr : fieldOk}`}
													placeholder="+389 2 ..."
												/>
												{phoneErr && (
													<p className="text-destructive text-xs mt-1">{req}</p>
												)}
											</div>
											<div>
												<label htmlFor={hotelId} className={labelClassName}>
													{t.contactPage.form.hotelLabel}{" "}
													<span className="text-destructive">*</span>
												</label>
												<select
													id={hotelId}
													name="hotel"
													value={hotel}
													onChange={(e) => setHotel(e.target.value)}
													className={`${fieldBase} ${hotelErr ? fieldErr : fieldOk}`}
												>
													<option value="">
														{t.contactPage.form.hotelDefault}
													</option>
													<option value="holiday-inn">
														{lang === "en"
															? "Holiday Inn Skopje"
															: "Holiday Inn Скопје"}
													</option>
													<option value="turist">
														{lang === "en" ? "Hotel Turist" : "Хотел Турист"}
													</option>
													<option value="karpos">
														{lang === "en" ? "Hotel Karposh" : "Хотел Карпош"}
													</option>
													<option value="bellevue">
														{lang === "en"
															? "Hotel Bellevue"
															: "Хотел Bellevue"}
													</option>
													<option value="vodno">
														{lang === "en" ? "Hotel Vodno" : "Хотел Водно"}
													</option>
												</select>
												{hotelErr && (
													<p className="text-destructive text-xs mt-1">{req}</p>
												)}
											</div>
										</div>

										<div className="flex-1 flex flex-col">
											<label htmlFor={messageId} className={labelClassName}>
												{t.contactPage.form.messageLabel}{" "}
												<span className="text-destructive">*</span>
											</label>
											<textarea
												id={messageId}
												name="message"
												value={message}
												onChange={(e) => setMessage(e.target.value)}
												className={`${fieldBase} ${messageErr ? fieldErr : fieldOk} resize-none flex-1 min-h-[100px]`}
												placeholder={t.contactPage.form.messagePlaceholder}
											/>
											{messageErr && (
												<p className="text-destructive text-xs mt-1">{req}</p>
											)}
										</div>

										<button
											type="submit"
											disabled={status === "loading"}
											className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-primary px-5 py-3 text-sm font-semibold text-white transition-all hover:bg-primary/90 hover:shadow-lg hover:shadow-primary/20 disabled:opacity-70 disabled:cursor-not-allowed"
										>
											{status === "loading"
												? lang === "en"
													? "Sending..."
													: "Се испраќа..."
												: t.contactPage.form.submit}
											<ArrowRight className="h-4 w-4" />
										</button>
										{status === "error" && (
											<p className="text-center text-xs text-destructive">
												{lang === "en"
													? "Something went wrong. Please try again."
													: "Се случи грешка. Обидете се повторно."}
											</p>
										)}
									</form>
								)}
							</div>
						</div>

						{/* Info */}
						<div className="h-full flex flex-col overflow-hidden rounded-xl md:rounded-2xl border border-border bg-card shadow-sm">
							<div className="h-1 bg-accent shrink-0" />
							<div className="p-5 sm:p-6 md:p-8 flex-1 flex flex-col">
								<div className="flex items-start justify-between gap-4">
									<div>
										<p className="text-xs font-bold tracking-[0.2em] uppercase text-accent">
											{t.contactPage.info.label}
										</p>
										<h2 className="mt-3 font-display text-xl sm:text-2xl font-medium tracking-tight text-primary">
											{t.contactPage.info.heading}
										</h2>
									</div>
									<div className="hidden sm:inline-flex h-10 w-10 items-center justify-center rounded-full bg-primary/5 text-primary">
										<Building2 className="h-5 w-5" />
									</div>
								</div>

								<div className="mt-6 rounded-xl border border-border bg-secondary p-4">
									<p className="text-xs font-bold tracking-[0.2em] uppercase text-primary">
										{t.contactPage.info.mainOfficeLabel}
									</p>
									<h3 className="mt-3 font-display text-lg font-medium text-primary">
										Македонијатурист АД
									</h3>
									<div className="mt-3 space-y-2 text-sm text-foreground/80 font-light">
										<div className="flex items-start gap-3">
											<MapPin className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary" />
											<span>ул. Филип Втори Македонски бр.5, 1000 Скопје</span>
										</div>
										<div className="flex items-center gap-3">
											<Phone className="h-4 w-4 flex-shrink-0 text-primary" />
											<a
												href="tel:+38923292731"
												className="hover:text-primary transition-colors"
											>
												+389 2 3292 731
											</a>
										</div>
										<div className="flex items-center gap-3">
											<Mail className="h-4 w-4 flex-shrink-0 text-primary" />
											<a
												href="mailto:maktur@makedonijaturist.com.mk"
												className="hover:text-primary transition-colors break-all"
											>
												maktur@makedonijaturist.com.mk
											</a>
										</div>
									</div>
								</div>

								<div className="mt-6 border-t border-border pt-6 flex-1 flex flex-col">
									<p className="text-xs font-bold tracking-[0.2em] uppercase text-primary">
										{t.contactPage.info.hotelsLabel}
									</p>
									<div className="mt-4 space-y-3 flex-1">
										{hotelContacts.map((hotel) => (
											<div
												key={hotel.nameEn}
												className="rounded-lg border border-border bg-background p-3"
											>
												<p className="text-sm font-medium text-primary">
													{lang === "en" ? hotel.nameEn : hotel.nameMk}
												</p>
												<div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-foreground/80 font-light">
													<a
														href={hotel.phoneHref}
														className="inline-flex items-center gap-1.5 hover:text-primary transition-colors"
													>
														<Phone className="h-3.5 w-3.5 text-primary" />
														<span>{hotel.phoneLabel}</span>
													</a>
													<a
														href={hotel.emailHref}
														className="inline-flex items-center gap-1.5 hover:text-primary transition-colors"
													>
														<Mail className="h-3.5 w-3.5 text-primary" />
														<span className="break-all">
															{hotel.emailLabel}
														</span>
													</a>
												</div>
											</div>
										))}
									</div>
								</div>
							</div>
						</div>
					</div>

					{/* Map - Full width below */}
					<div className="mt-6 mt-force-light overflow-hidden rounded-xl md:rounded-2xl border border-border bg-card shadow-sm">
						<div className="h-1 bg-accent" />
						<div className="overflow-hidden rounded-b-xl md:rounded-b-2xl">
							<MapView
								center={hotelMapCenter}
								zoom={12}
								initialView={hotelInitialView}
								scrollZoom={false}
								className="h-[280px] sm:h-[350px] md:h-[400px]"
							>
								{hotels.map((hotel) => (
									<MapMarker
										key={hotel.name}
										longitude={hotel.longitude}
										latitude={hotel.latitude}
									>
										<MarkerContent>
											<button
												type="button"
												aria-label={lang === "en" ? hotel.nameEn : hotel.name}
												className="group relative grid h-9 w-9 place-items-center rounded-full focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-ring/30"
											>
												<span className="absolute inset-0 rounded-full bg-accent/25 blur-sm opacity-0 transition-opacity group-hover:opacity-100" />
												<span className="relative grid h-3.5 w-3.5 place-items-center rounded-full bg-accent ring-4 ring-background shadow-sm transition-transform group-hover:scale-110">
													<span className="h-1.5 w-1.5 rounded-full bg-primary" />
												</span>
											</button>
										</MarkerContent>
										<MarkerPopup>
											<p className="font-display text-base font-medium tracking-tight text-primary">
												{lang === "en" ? hotel.nameEn : hotel.name}
											</p>
											<p className="mt-1 text-xs text-muted-foreground font-light leading-relaxed">
												{hotel.address}
											</p>
											<a
												href={`https://www.google.com/maps?q=${hotel.latitude},${hotel.longitude}`}
												target="_blank"
												rel="noreferrer"
												className="mt-3 inline-flex items-center gap-2 text-xs font-semibold text-primary transition-colors hover:text-primary/80"
											>
												{t.contactPage.map.directions}
												<ArrowRight className="h-3 w-3" />
											</a>
										</MarkerPopup>
									</MapMarker>
								))}
							</MapView>
						</div>
					</div>
				</div>
			</section>
		</main>
	);
}
